import React, { FC, useContext, useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { AppContext } from "Context/AppContext";
import DeleteIcon from "Components/Common/components/Delete_icon";
import Label from "Components/Common/components/Label";
import { Link } from "react-router-dom";
import { ADD_NEW, CANCEL, ITEMS, LISTS, LISTS_ROUTE, LIST_CREATE, LIST_NAME, LIST_READ, LIST_UPDATE, ListsEditHeaders, SAVE, TAXONOMY, } from "./constants";
import { AddIconWithBorder, DragDropIcon, ImageIcon, InfoIcon, LeftVectorIcon } from "assests/GmsIcons";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import ToggleButton from "Components/Common/components/Inputs/ToggleButton";
import ProductAttributeImport from "../ISR/Pages/DefineModel/Components/ProductAttributeImport";
import useProductAttributeImport from "../ISR/Pages/DefineModel/Hooks/useProductAttributeImport";
import TableSkeletonElement from "Components/GMS/Products/components/TableSkeletonElement";
import { toast } from "react-toastify";
import OverlayIcon from "Components/GMS/Rules/Components/OverlayIcon";
import useTable from "Components/Common/Table/useTable";
import HelpTextDialog from "./HelpTextDialog";
import { HelpTextData } from "../ISR/Pages/DefineModel/model";
import { Actions } from "./Actions";
import { exportList, getListItem, saveList, updateList } from "./Services/ListServices";
import ValidateModal from "./ValidateModal";

interface Row {
  title: string;
  display_value: string;
  visibility: boolean;
  image: string;
  id?: string;
  math_value: number | null
  helpText?: HelpTextData;
}

interface ListObj {
  id?: string;
  name: string;
  industry_id: string;
  items: Row[];
  imported_attribute: string;
  created_at: string;
  created_by: string;
  updated_at?: string;
  updated_by?: string;
}

const ListsEdit: FC = (props: any) => {
  let appContext = useContext(AppContext);
  let industryId = appContext && appContext?.get_selected_industry() && appContext?.get_selected_industry()?.id;
  const { params } = props.match;
  const { editId } = params;
  const [loading, setLoading] = useState(false);
  const appData = useContext(AppContext);
  const history = useHistory();
  const [tableData, setTableData] = useState<Row[]>();
  const [models, setModels] = useState<string[]>([]);
  const [listName, setListName] = useState<string>();
  const [editState, setEditState] = useState<ListObj>();
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const { showAttributeMenu, setShowAttributeMenu, attributeData, selectedAttribute, setSelectedAttribute, selectedItems, setSelectedItems, fieldDataConfiguration, setFieldDataConfiguration, filteredAttributeData, openAttributeMenu, } =
    useProductAttributeImport({ attributeId: editState?.imported_attribute as string });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const { headers, sort, handleSortByColumn } =
    useTable({ table_headers: ListsEditHeaders });
  const [showImagePopup, setShowImagePopup] = useState(false)
  const [imageIndex, setImageIndex] = useState<number>()

  const [lastTableRowRef, setLastTableRowRef] = useState<any>();
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [showValidateModal, setShowValidateModal] = useState<boolean>(false);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined);
  const createPermission = appContext.pm.checkPermission(LIST_CREATE);
  const readPermission = appContext.pm.checkPermission(LIST_READ);
  const updatePermission = appContext.pm.checkPermission(LIST_UPDATE);

  async function doExportList() {
    try {
      if (!editState?.id) return
      const res = await exportList(editState?.id)
      if (res?.done) {
        setDownloadUrl(res?.data?.url);
        toast.success("Downloaded successfully");
      }
      else throw new Error("Download Failed");
    }
    finally {
      setTimeout(() => {
        setDownloadUrl(undefined);
      }, 4000)
    }
  }

  const onOpenImportModal = () => setShowImportModal(true)
  const onCloseImportModal = () => setShowImportModal(false)
  const onOpenValidateModal = () => setShowValidateModal(true)
  const onCloseValidateModal = () => setShowValidateModal(false)
  const onExport = () => {
    doExportList();
  }
  const modalRoot = document.querySelector('#modal-root');

  useEffect(() => {
    if (lastTableRowRef) {
      lastTableRowRef?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [lastTableRowRef]);


  useEffect(() => {
    if (!readPermission) {
      history.push("/workspace/module/permission/denied");
    }
  }, [appData.channelKey]);

  useEffect(() => {
    const table: Row[] = fieldDataConfiguration.choices?.map((choice) => ({
      title: choice.av_value,
      display_value: choice.av_value,
      visibility: true,
      image: "",
      math_value: null
    })) || [];
    setTableData((prev: Row[] | undefined) => {
      if (!prev) return table;
      const newTable = table.filter(x => {
        if (!prev?.find(y => y.title === x.title)) return x
      })
      return [...prev, ...newTable];
    });
  }, [fieldDataConfiguration]);

  useEffect(() => {
    if (![null, "null", "undefined", undefined].includes(editId)) { fetchEditState(); }
  }, []);

  const fetchEditState = async () => {
    try {
      setLoading(true);
      const res = await getListItem(editId);
      if (res?.data?.done) {
        setListName(res?.data?.data?.name);
        setTableData(res?.data?.data?.items);
        setModels(res?.data?.data?.models);
        setEditState(res?.data?.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(tableData ?? []);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setTableData(items);
  };

  const onInputChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
    type: string
  ) => {
    event.persist();
    const val = event?.target?.value;
    setTableData((prev) => {
      const newValue = prev?.map((entry, id) => {
        if (id === index) {
          switch (type) {
            case "title":
              entry.title = val;
              break;
            case "display_value":
              entry.display_value = val;
              break;
            case "math_value":
              if (isNaN(Number(val))) {
                toast.info("Math value must be an integer")
                break
              }
              entry.math_value = Number(val);
              break;
            default:
              break;
          }
        }
        return entry;
      });
      return newValue;
    });
  };

  const onVisibilityChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    setTableData((prev) => {
      const newValue = prev?.map((entry, id) => {
        if (id === index) entry.visibility = !entry?.visibility;
        return entry;
      });
      return newValue;
    });
  };

  const handleSave = async () => {
    try {
      if (createPermission) {
        if (listName) {
          setLoading(true)
          const requestBody = {
            name: listName,
            items: tableData,
            industry_id: industryId,
            imported_attribute: fieldDataConfiguration?.attributeId
          };
          const save = await saveList(requestBody);
          setLoading(false)
          if (save?.data.done === true) {
            toast.success(save?.data.message ?? "List Saved Succesfully")
            if (save?.data?.data?.id) {
              history.replace({
                pathname: `lists/edit/${save?.data?.data?.id}`,
              });
            }
            else {
              history.push("/gms/taxonomy/lists")
            }
          } else {
            toast.error(save?.data.message ?? "Failed to create list")
          }
        }
        else {
          toast.info("List name is mandatory")
        }
      }
      else toast.info("You don't have the permission to create list")
    } catch (error) {
      console.log(error);
    }
  };

  const handleEditSave = async () => {
    if (!editState?.id) return
    try {
      if (updatePermission) {

        setLoading(true)
        if (listName) {
          const requestBody = {
            id: editState?.id,
            name: listName,
            items: tableData,
            industry_id: industryId,
            imported_attribute: fieldDataConfiguration?.attributeId
          };
          const update = await updateList(requestBody, editState?.id);
          if (!update?.errorfound)
            toast.success("List Updated Succesfully")
        }
        else {
          toast.info("List name is mandatory")
        }
      }
      else toast.info("You don't have the permission to update")
    } catch (error) {
      console.log(error);
    }
    finally {
      setLoading(false)
    }
  };

  const convertToDataUrl = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  async function handle_file_selection(event: any, index: number) {
    event.preventDefault();
    const file = event.target.files[0];
    setSelectedFile(file);
    const image = await convertToDataUrl(file);
    setTableData((prev) => {
      const newValue = prev?.map((entry, id) => {
        if (id === index) entry.image = image as string;
        return entry;
      });
      return newValue;
    });
  }

  const handleButtonClick = () => {
    fileInputRef?.current?.click();
  };

  const handleDelete = (e: any, index: number) => {
    if (appData?.pm?.checkPermission("listmodule_delete")) {

      setTableData((prev) => {
        const newData = prev?.filter((x, ind) => ind !== index);
        return newData;
      });
    }
    else toast.info("You don't have the permission to delete")
  };

  const handleHelpText = (deleteRequested?: boolean, helpTextData?: HelpTextData) => {
    if (updatePermission) {
      setTableData((prev) => {
        const newData = prev?.map((x, ind) => {
          if (currentIndex === ind) {
            if (deleteRequested === true) {
              delete x.helpText;
              return x;
            }
            return { ...x, helpText: helpTextData };
          }
          return x
        });
        return newData;
      });
    }
    else toast.info("You don't have the permission to delete")
    setCurrentIndex(null);
  };

  const handleAdd = () => {
    setTableData((prev) => {
      const newData = [
        ...(prev || []),
        { title: "", display_value: "", visibility: true, image: "", math_value: null },
      ];
      return newData;
    });
  };

  const checkAttributeValue = (val: string) => {
    const values = fieldDataConfiguration?.choices?.map((x) => x?.value);
    return values?.includes(val);
  };

  return (
    <React.Fragment>
      <div className="tw-mt-1">
        <nav className="tw-flex" aria-label="Breadcrumb">
          <ol className="tw-inline-flex tw-items-center tw-space-x-1 tw-md:space-x-2 tw-rtl:space-x-reverse">
            <li className="tw-inline-flex tw-items-center tw-pointer-events-none">
              <Label
                message={TAXONOMY}
                fontSize={"0.75"}
                fontWeight="400"
                color="#5c5f62"
              />
            </li>
            <li>/</li>
            <li className="tw-inline-flex tw-items-center">
              <Link to={LISTS_ROUTE}>
                <Label
                  message={LISTS}
                  fontSize={"0.75"}
                  fontWeight="500"
                  color="#5c5f62"
                />
              </Link>
            </li>
            <li>/</li>
            <li className="tw-inline-flex tw-items-center tw-pointer-events-none" aria-current="page">
              <Label
                message={listName}
                fontSize={"0.75"}
                fontWeight="500"
                color="#5c5f62"
              />
            </li>
          </ol>
        </nav>
      </div>
      <div className={`tw-px-[21px] tw-pt-[12px] tw-flex tw-justify-between`}>
        <Link
          to={"/gms/taxonomy/lists"}
          className="tw-float-left tw-w-[4%] tw-mt-1.5"
        >
          <LeftVectorIcon />
        </Link>
        <div
          className={`tw-flex tw-flex-col tw-items-left tw-text-xs tw-w-[96%]`}
        >
          <label
            className={`tw-float-right tw-text-black tw-font-noto-sans tw-text-xl tw-font-semibold tw-leading-[25.2px]`}
          >
            {LISTS}
          </label>
        </div>
        <div className={`tw-flex`}>
          <div>
            <button
              onClick={() => {
                history.push("/gms/taxonomy/lists")
              }}
              className="tw-bg-white tw-text-blue-800 tw-border tw-border-blue-800 tw-hover:bg-blue-400 tw-font-bold tw-py-2 tw-px-4 tw-ml-2 tw-mr-2 tw-rounded tw-items-center"
            >
              {CANCEL}
            </button>
          </div>
          <div>
            <button
              onClick={handleEditSave}
              className="tw-bg-blue-800 tw-text-white tw-hover:bg-blue-400 tw-font-bold tw-py-2 tw-px-4 tw-ml-2 tw-mr-2 tw-rounded tw-items-center"
              disabled={loading}
            >
              {SAVE}
            </button>
          </div>
        </div>
      </div>
      <div
        className={`tw-ml-5 tw-mt-2 tw-box-border tw-w-auto tw-border-t-[1px] tw-border-solid tw-border-lightgray-200`}
      />
      {loading ? (
        <TableSkeletonElement />
      ) : (
        <>
          <div className="tw-flex tw-mt-2 tw-mb-2">
            <div className="tw-w-[30%]">
              <label className="tw-text-black tw-text-sm tw-ml-4 tw-mb-2">
                {LIST_NAME}
                <span className="tw-text-red-500 tw-ml-2">*</span>
              </label>
              <input
                placeholder="Enter Name"
                className=" tw-placeholder-gray-200 tw-ml-4 tw-mb-4 tw-w-[364px] tw-h-9 tw-bg-white tw-rounded tw-border tw-border-zinc-300 tw-p-2"
                value={listName}
                onChange={(e) => {
                  setListName(e?.target?.value);
                }}
              />
            </div>
            <div className="tw-w-[70%] tw-ml-16">
              <label className="tw-text-black tw-text-sm tw-ml-4 tw-mb-1 tw-w-full">
                Associated Models
              </label>
              {models?.length > 0 ? models?.map((model, index) =>
                <div key={index} className="tw-px-[8px] tw-bg-[#f4f5f7] tw-m-1 tw-rounded-sm tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                  <div className="tw-text-[#172b4d]  tw-text-xs tw-w-auto">{model}</div>
                </div>) :
                <div className="tw-text-[#172b4d] tw-ml-4 tw-text-xs">
                  No Models Associated
                </div>}
            </div>
          </div>
          <div
            ref={(el) => {
              setLastTableRowRef(el)
            }}
            className="tw-ml-4 tw-w-[97%] tw-h-[50%] tw-bg-white tw-rounded tw-border tw-border-gray-200">
            <div className="tw-flex tw-justify-between">
              <div className="tw-text-slate-500 tw-text-s tw-font-bold tw-font-['Noto Sans'] tw-leading-tight tw-mt-4 tw-mb-4 tw-ml-2">
                {ITEMS}
              </div>
              {!showAttributeMenu && <Actions
                onOpenImportModal={onOpenImportModal}
                onOpenValidateModal={onOpenValidateModal}
                onExport={onExport}
                disabled={false}
                openAttributeMenu={openAttributeMenu}
              />}
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="rows">
                {(provided) => (
                  <div className="tw-max-h-[510px] tw-overflow-x-auto ">
                    <table
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className=" tw-table-auto tw-w-full tw-overflow-scroll"
                    >
                      <thead className=" tw-sticky tw-top-0 tw-bg-zinc-200  tw-border-zinc-200 tw-h-12 tw-font-noto-sans  tw-mt-[42px] tw-mb-[12px] tw-border tw-z-40">
                        <tr >
                          {headers.map(
                            (
                              {
                                label,
                                ...rest
                              }: {
                                label: string;
                                options: any;
                                map: string;
                              },
                              index: number
                            ) => {
                              return (
                                <th
                                  key={`${label}_${index}`}
                                  className=" tw-sticky tw-text-slate-500 tw-text-sm tw-font-semibold tw-font-['Noto Sans']"

                                  scope="col"
                                >
                                  <div
                                    className="tw-flex tw-align-middle"
                                    onClick={() => {
                                      rest.options?.sort &&
                                        handleSortByColumn(rest.map);
                                    }}
                                  >
                                    <label className="tw-text-[#5c5f62]">
                                      {label}
                                      {rest.options?.infoMessage && (
                                        <span className="tw-ml-1 tw-inline-block">
                                          <OverlayIcon
                                            message={rest.options?.infoMessage ?? "Display value will show on the runtime in any application. If its empty it shows the default title. "}
                                            placement="bottom"
                                          />
                                        </span>
                                      )}
                                    </label>
                                    &nbsp;
                                    <span className="tw-ms-2">
                                      {rest.options?.sort &&
                                        (sort.column === rest.map ? (
                                          <i
                                            className={`fas fa-sort-${sort.direction === "asc"
                                              ? "up"
                                              : "down"
                                              }`}
                                          />
                                        ) : (
                                          <i className={"fas fa-sort"} />
                                        ))}
                                    </span>
                                  </div>
                                </th>
                              );
                            }
                          )}
                        </tr>
                      </thead>
                      <tbody >
                        {tableData?.map((item: Row, index) => {
                          const hasHelpText = Object.prototype.hasOwnProperty.call(item, "helpText")
                          return (
                            <Draggable
                              key={index}
                              draggableId={`${index}`}
                              index={index}
                              isDragDisabled={false}
                            >
                              {(provided) => (
                                <tr
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`tw-w-full tw-h-12 tw-bg-white  tw-border tw-border-zinc-200`}
                                >
                                  <td
                                    className={`tw-h-12 ${index % 2 === 0
                                      ? "tw-bg-white"
                                      : "tw-bg-gray-100"
                                      } tw-pl-4 tw-pr-4`}
                                  >
                                    <DragDropIcon />
                                  </td>
                                  <td
                                    className={`tw-h-12 tw-max-h-12 ${index % 2 === 0
                                      ? "tw-bg-white"
                                      : "tw-bg-gray-100"
                                      } tw-cursor-pointer`}
                                    onClick={() => {
                                      setShowImagePopup(true);
                                      setSelectedFile(tableData[index]?.image)
                                      setImageIndex(index);
                                    }}
                                  >
                                    {item?.image ? (
                                      <img
                                        src={item?.image}
                                        alt="image"
                                        className="tw-h-12 tw-w-12"
                                      />
                                    ) : (
                                      <ImageIcon />
                                    )}
                                  </td>
                                  <td
                                    className={`tw-h-12 ${index % 2 === 0
                                      ? "tw-bg-white"
                                      : "tw-bg-gray-100"
                                      } tw-w-[30%]`}
                                  >
                                    <input
                                      className={`tw-placeholder-gray-200 tw-w-[95%] tw-h-9 tw-px-2.5 tw-py-2 ${checkAttributeValue(item?.title)
                                        ? "tw-bg-gray-100"
                                        : "tw-bg-white"
                                        }  tw-rounded tw-border tw-border-zinc-300 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex `}
                                      onChange={(e) =>
                                        onInputChangeHandler(e, index, "title")
                                      }
                                      value={item.title}
                                      disabled={checkAttributeValue(item?.title)}
                                      placeholder="Enter Title"
                                    />
                                  </td>
                                  <td
                                    className={`tw-h-12 ${index % 2 === 0
                                      ? "tw-bg-white"
                                      : "tw-bg-gray-100"
                                      } tw-w-[30%]`}
                                  >
                                    <input
                                      placeholder="Enter Display Value"
                                      className={`tw-placeholder-gray-200  tw-w-[95%] tw-h-9 tw-px-2.5 tw-py-2 tw-bg-white tw-rounded tw-border tw-border-zinc-300 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex `}
                                      onChange={(e) =>
                                        onInputChangeHandler(
                                          e,
                                          index,
                                          "display_value"
                                        )
                                      }
                                      value={item.display_value}
                                    />
                                  </td>
                                  <td
                                    className={`tw-h-12 ${index % 2 === 0
                                      ? "tw-bg-white"
                                      : "tw-bg-gray-100"
                                      } tw-w-[20]`}
                                  >
                                    <input
                                      placeholder="Enter Math Value"
                                      className={`tw-placeholder-gray-200  tw-w-[95%] tw-h-9 tw-px-2.5 tw-py-2 tw-bg-white tw-rounded tw-border tw-border-zinc-300 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex `}
                                      onChange={(e) =>
                                        onInputChangeHandler(
                                          e,
                                          index,
                                          "math_value"
                                        )
                                      }
                                      type="number"
                                      value={item.math_value as number}
                                    />
                                  </td>
                                  <td
                                    className={`tw-h-12 ${index % 2 === 0
                                      ? "tw-bg-white"
                                      : "tw-bg-gray-100"
                                      }`}
                                  >
                                    <ToggleButton
                                      value={item?.visibility}
                                      name={`toggle-${index}`}
                                      onChange={(e) =>
                                        onVisibilityChangeHandler(e, index)
                                      }
                                    />
                                  </td>
                                  <td className={`tw-h-12 ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"}`}>
                                    <button
                                      className="tw-cursor-pointer tw-flex tw-align-middle tw-text-danger"
                                      onClick={(e) => {
                                        setCurrentIndex(index)
                                      }}
                                    >
                                      {hasHelpText ?
                                        <div className="tw-p-2.5"><InfoIcon color={'#0747A6'} /></div> :
                                        <AddIconWithBorder />
                                      }
                                    </button>
                                  </td>
                                  {!checkAttributeValue(item?.title) ? (
                                    <td
                                      className={`tw-h-12 ${index % 2 === 0
                                        ? "tw-bg-white"
                                        : "tw-bg-gray-100"
                                        }`}
                                    >
                                      <button
                                        className=" tw-cursor-pointer tw-flex tw-align-middle tw-text-danger"
                                        onClick={(e) => {
                                          handleDelete(e, index);
                                        }}
                                      >
                                        <DeleteIcon />
                                      </button>
                                    </td>
                                  ) : (
                                    <td
                                      className={`tw-h-12 ${index % 2 === 0
                                        ? "tw-bg-white"
                                        : "tw-bg-gray-100"
                                        }`}
                                    ></td>
                                  )}
                                </tr>
                              )}
                            </Draggable>
                          );
                        })}

                        {provided.placeholder}
                      </tbody>
                    </table>
                  </div>

                )}
              </Droppable>
            </DragDropContext>
            <button
              className="tw-bg-white tw-text-blue-600 tw-m-4"
              onClick={handleAdd}
            >
              {ADD_NEW}
            </button>
          </div>
        </>
      )}
      {showAttributeMenu && (
        <ProductAttributeImport
          attributeData={attributeData}
          showAttributeMenu={showAttributeMenu}
          setShowAttributeMenu={setShowAttributeMenu}
          filteredAttributeData={filteredAttributeData}
          selectedAttribute={selectedAttribute}
          setSelectedAttribute={setSelectedAttribute}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          fieldDataConfiguration={fieldDataConfiguration}
          setFieldDataConfiguration={setFieldDataConfiguration}
        />
      )}
      {showImagePopup && (
        <div className=" tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-40 tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
          <div className="tw-w-[613px] tw-h-[280px] tw-bg-white tw-rounded tw-shadow tw-border tw-border-neutral-300">
            <div className=" tw-font-noto-sans tw-text-sm tw-font-bold tw-m-2">
              Item Image
            </div>
            <div className="tw-w-[100%] tw-h-[0px] tw-border tw-border-zinc-200"></div>
            <div>
              <div className="tw-flex tw-align-middle tw-m-2 tw-mt-4">
                <div className="tw-p-1 tw-ml-2 tw-mr-2 tw-border">
                  {(tableData && tableData[imageIndex as number] && tableData[imageIndex as number]?.image) ?
                    <img alt='attr-value-image' src={tableData &&
                      tableData[imageIndex as number]?.image}
                      className="tw-w-[59px] tw-h-[59px] tw-align-middle tw-justify-center"
                    />
                    :
                    <ImageIcon width={59} height={59} />
                  }</div>
                <div className="tw-ml-2">
                  <div className="tw-m-1">
                    {selectedFile ? selectedFile?.name : (tableData && tableData[imageIndex as number] && tableData[imageIndex as number]?.image) ? "" : "No image available"}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className=" tw-hidden"
                    onChange={(event) => {
                      handle_file_selection(event, imageIndex as number);
                    }}
                  />
                  <button
                    onClick={handleButtonClick}
                    className=" tw-h-8 tw-px-[22px] tw-py-2 tw-bg-white tw-rounded tw-border tw-border-blue-800 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex"
                  >
                    <span className="tw-text-center tw-text-blue-800 tw-text-sm tw-font-normal font-['Noto Sans'] leading-tight">
                      {"Browse File"}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div className=" tw-ml-4 tw-mt-6 tw-text-slate-500 tw-text-xs tw-font-bold tw-font-['Noto Sans'] tw-leading-tight">
              Image URL
            </div>
            <input
              placeholder="Enter Url"
              className="tw-p-2 tw-ml-4 tw-mr-4 tw-mt-3 tw-mb-2 tw-w-[95%] tw-h-9 tw-bg-white tw-rounded tw-border tw-border-zinc-300"
              onChange={(e) => {
                e.preventDefault()
                const url = e?.target?.value
                setTableData((prev) => {
                  const newValue = prev?.map((entry, id) => {
                    if (id === imageIndex) entry.image = url
                    return entry;
                  });
                  return newValue;
                });
              }}
              value={tableData &&
                tableData[imageIndex as number].image
              }
            ></input>
            <div className="tw-float-right">
              <button onClick={() => { setShowImagePopup(false) }} className="tw-m-2 tw-h-8 tw-px-[22px] tw-py-2 tw-bg-white tw-rounded tw-border tw-border-blue-800 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                <span className="tw-text-center tw-text-blue-800 tw-text-sm tw-font-normal font-['Noto Sans'] leading-tight">
                  {CANCEL}
                </span>
              </button>
              <button onClick={() => {
                setShowImagePopup(false)
                setImageIndex(undefined)
                setSelectedFile(null)

              }} className=" tw-h-8 tw-m-2 tw-px-[22px] tw-py-2 tw-text-white tw-rounded tw-border tw-bg-blue-800 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                <span className="tw-text-center tw-text-white tw-text-sm tw-font-normal font-['Noto Sans'] leading-tight">
                  {SAVE}
                </span>
              </button>
            </div>

          </div>
        </div>

      )}
      {currentIndex != null ? <HelpTextDialog selectedItemId={editState?.id}
        savedHelpTextData={tableData != null ? tableData[currentIndex].helpText : null}
        onDelete={() => handleHelpText(true)}
        onClose={() => setCurrentIndex(null)}
        onSubmit={(helpTextData) => handleHelpText(false, helpTextData)} /> : null}
      {(showValidateModal || showImportModal) && <ValidateModal isOpen={showImportModal === true ? showImportModal : showValidateModal} onClose={showImportModal === true ? onCloseImportModal : onCloseValidateModal} listId={editState?.id} action={showImportModal ? "Import" : "Validate"} key={showImportModal ? 'import-modal' : 'validate-modal'} fetchEditState={fetchEditState} />}
      {modalRoot && <iframe style={{ display: "none" }} src={downloadUrl} id="iframe_download_list_template" title="Download Template" />}
    </React.Fragment>
  );
};


export default ListsEdit;

